import { Grid } from '@mui/material';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import ReactMarkdown from 'react-markdown';
import { connect } from 'react-redux';
import ShowMoreText from 'react-show-more-text';
import { bindActionCreators } from 'redux';
import ExpandableListItem from '/@/components/map/expandable-list-item';
import ReferenceItems from '/@/components/map/reference-items';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { setVersionLabel } from '/@/store/maps/actions';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

@autobind
class ItemOverlayTitle extends Component {
    static propTypes = {
        label: PropTypes.string,
    };
    render() {
        const { label } = this.props;
        return (
            <div className={styles.sectionTitle}>
                <div className={styles.blackDot} />
                {_(label)}
            </div>
        );
    }
}

@autobind
class ColoredMetrics extends Component {
    static propTypes = {
        metrics: PropTypes.array,
        choices: PropTypes.object,
        colorChoices: PropTypes.object,
    };
    render() {
        const { metrics, choices, colorChoices } = this.props;
        return (
            <div className={styles.metrics}>
                {metrics.map((metric) => (
                    <div key={metric.id} className={styles.metric}>
                        <div className={styles.sectionTitle}>{_(metric.name)}</div>
                        <div className={classNames(styles.metricValue, styles[colorChoices[metric.value]])}>
                            {metric.value ? choices[metric.value] : '-'}
                        </div>
                    </div>
                ))}
            </div>
        );
    }
}
@connect(
    (state) => ({}),
    (dispatch) => ({
        ...bindActionCreators({ setVersionLabel }, dispatch),
        dispatch,
    })
)
@autobind
export default class ItemOverlay extends Component {
    static propTypes = {
        overlay: PropTypes.object,
        version: PropTypes.object,
        values: PropTypes.array,
        onOverlayChange: PropTypes.func,
    };

    handleVersionChange(value, option) {
        this.props.onOverlayChange({
            type: this.props.overlay.slug,
            version: value,
        });

        if (option && option.label) this.props.setVersionLabel(option.label);
    }

    renderVersionSelect(versions) {
        if (this.props.values) {
            versions = versions.filter(({ referenceItems }) =>
                referenceItems.some(({ id }) => this.props.values.includes(id))
            );
        }

        const options = versions.map((v) => ({
            label: _(v.versionLabel),
            value: v.versionNumber,
        }));

        return (
            <Select
                appearance='outline'
                color={settings.app.theme}
                size='sm'
                value={this.props.version.versionNumber}
                options={options}
                onChange={this.handleVersionChange}
                placeholder={_('Seleccione una version')}
                className={styles.versionSelect}
            />
        );
    }

    string_to_slug(str) {
        str = str.replace(/^\s+|\s+$/g, ''); // trim
        str = str.toLowerCase();

        // remove accents, swap ñ for n, etc
        const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;';
        const to = 'aaaaeeeeiiiioooouuuunc------';
        for (let i = 0, l = from.length; i < l; i++) {
            str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
        }
        str = str
            .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
            .replace(/\s+/g, '-') // collapse whitespace and replace by -
            .replace(/-+/g, '-'); // collapse dashes

        return str;
    }

    render() {
        const name = _(this.props.overlay.name);
        const scientificName = _(this.props.overlay.scientificName);
        const unit = _(this.props.version.referenceUnit);
        const source = _(this.props.version.source);
        const symptoms = _(this.props.version.symptoms);
        const managementRecommendations = _(this.props.version.managementRecommendations);
        const items = this.props.version.mapValues || this.props.version.referenceItems;
        const confirmedResistance = this.props.version.confirmedResistance;
        const metrics = this.props.version.mapMetrics || [];

        const metrics_by_group = metrics.reduce((storage, metric) => {
            const group = metric.group;
            storage[group] = storage[group] || [];
            storage?.[group]?.push(metric);
            return storage;
        }, {});

        const textColorChoices = {
            low: 'green',
            medium: 'yellow',
            high: 'red',
        };

        const PerformanceImpactChoices = {
            low: _('bajo'),
            medium: _('medio'),
            high: _('alto'),
        };

        const WeedMetricsChoices = {
            low: _('baja probabilidad'),
            medium: _('media probabilidad'),
            high: _('alta probabilidad'),
        };

        const hibernationAndMigrationChoices = {
            low: _('no'),
            medium: _('parcial'),
            high: _('sí'),
        };

        const controlStrategiesChoices = {
            low: _('Controla'),
            medium: _('Controla parcialmente'),
            high: _('No controla'),
        };

        const controlMetricChoices = {
            low: _('Control efectivo'),
            medium: _('Control medianamente efectivo'),
            high: _('Control no efectivo'),
        };

        const getGroupMetrics = (label) => {
            if (!Object.keys(metrics_by_group).length) return undefined;
            if (!Object.keys(metrics_by_group).includes(label)) return undefined;
            if (!metrics_by_group?.[label]?.length) return undefined;
            return metrics_by_group?.[label]?.sort((a, b) => {
                return a.order - b.order;
            });
        };

        const probabilityOfOccurrenceMetrics = getGroupMetrics('probability_of_occurrence');
        const performanceImpactMetrics = getGroupMetrics('performance_impact');
        const hibernationAndMigrationMetrics = getGroupMetrics('hibernation_and_migration');
        const controlStrategiesMetrics = getGroupMetrics('control_strategies');
        const resistanceMetrics = getGroupMetrics('resistence');
        const fallowManagementMetrics = getGroupMetrics('fallow_management');
        const chemicalControlStrategiesMetrics = getGroupMetrics('chemical_control_strategies');

        const images =
            this.props.version.images &&
            this.props.version.images
                .slice(0, 3)
                .map((image) => <img key={image.id} src={image.file} alt={scientificName} className={styles.image} />);

        return (
            <div className={styles.root}>
                <Grid container justifyContent={'space-between'}>
                    <Grid item>
                        <div className={classNames(styles.handle, 'handle')}>
                            <i className='material-icons'>drag_handle</i>
                        </div>
                    </Grid>
                    <Grid item>
                        <div className={styles.close} onClick={this.props.onOverlayClose}>
                            <i className='material-icons'>close</i>
                        </div>
                    </Grid>
                </Grid>
                <div className={styles.header}>
                    <div className={styles.name}>{name}</div>
                    {scientificName && <div className={styles.scientificName}>{scientificName}</div>}
                </div>
                {symptoms && (
                    <ShowMoreText
                        lines={5}
                        more={_('Ver más')}
                        less={_('Ver menos')}
                        className={styles.description}
                        anchorClass={styles.showMore}
                        expanded={false}
                    >
                        {symptoms}
                    </ShowMoreText>
                )}
                {images && !!images.length && <div className={styles.imageContainer}>{images}</div>}
                <div className={styles.overlaySection}>
                    <div className={styles.whiteLine} />
                    {this.props.overlay.type === 'humidity' ? (
                        <Fragment>
                            <div className={styles.sectionTitle}>
                                <div className={styles.blackDot} />
                                {_('Fecha')}
                            </div>
                            {this.renderVersionSelect(this.props.overlay.versions)}
                            <div className={styles.sectionTitle}>
                                <div className={styles.blackDot} />
                                {_('Referencias')}
                            </div>
                            <ReferenceItems items={items} separated />
                        </Fragment>
                    ) : (
                        <Fragment>
                            <div className={styles.sectionTitle}>
                                <div className={styles.blackDot} />
                                {_('Referencias')}
                            </div>
                            {this.renderVersionSelect(this.props.overlay.versions)}
                            <ReferenceItems items={items} separated={false} />
                        </Fragment>
                    )}
                    {unit && (
                        <div className={styles.unit}>
                            <span> {`${_('Unidad:')} `}</span>
                            <ReactMarkdown className={styles.unitMd}>{unit}</ReactMarkdown>
                        </div>
                    )}
                    {source && <div className={styles.description}>{`${_('Fuente:')} ${source}`}</div>}
                </div>
                {probabilityOfOccurrenceMetrics && !!probabilityOfOccurrenceMetrics.length && (
                    <ExpandableListItem
                        expanded={false}
                        label={<ItemOverlayTitle label={_('Probabilidad de ocurrencia')} />}
                    >
                        <div className={styles.probabilities}>
                            {probabilityOfOccurrenceMetrics.map((metric) => (
                                <div key={metric.id} className={styles.probability}>
                                    <div className={styles.probabilityName}>{_(metric.name)}</div>
                                    <div
                                        className={classNames(
                                            styles.probabilityValue,
                                            styles[textColorChoices[metric.value]]
                                        )}
                                    >
                                        {PerformanceImpactChoices[metric.value]}
                                    </div>
                                    <div
                                        className={classNames(
                                            styles.svg,
                                            styles[this.string_to_slug(`${metric.name.es}-${metric.value}`)],
                                            styles[this.string_to_slug(`${metric.name.en}-${metric.value}`)],
                                            styles[this.string_to_slug(`${metric.name.pt}-${metric.value}`)]
                                        )}
                                    />
                                </div>
                            ))}
                        </div>
                    </ExpandableListItem>
                )}
                {performanceImpactMetrics && !!performanceImpactMetrics.length && (
                    <ExpandableListItem
                        expanded={false}
                        label={<ItemOverlayTitle label={_('Impacto en el rendimiento')} />}
                    >
                        {performanceImpactMetrics.map((metric) => (
                            <div
                                key={metric.id}
                                className={classNames(styles.impactValue, styles[textColorChoices[metric.value]])}
                            >
                                {PerformanceImpactChoices[metric.value]}
                            </div>
                        ))}
                    </ExpandableListItem>
                )}
                {(resistanceMetrics?.length > 0 || _(confirmedResistance) !== '') && (
                    <ExpandableListItem expanded={false} label={<ItemOverlayTitle label={_('Resistencia')} />}>
                        {resistanceMetrics?.length > 0 &&
                            resistanceMetrics.map((metric) => (
                                <div
                                    key={metric.id}
                                    className={classNames(styles.impactValue, styles[textColorChoices[metric.value]])}
                                >
                                    {_(PerformanceImpactChoices[metric.value])}
                                </div>
                            ))}
                        {_(confirmedResistance) !== '' && (
                            <div className={styles.managementRecommendation}>
                                <ReactMarkdown className={styles.resistance}>{_(confirmedResistance)}</ReactMarkdown>
                            </div>
                        )}
                    </ExpandableListItem>
                )}
                {hibernationAndMigrationMetrics && !!hibernationAndMigrationMetrics.length && (
                    <div className={styles.overlaySection}>
                        <div className={styles.whiteLine} />
                        <div className={styles.migrationMetrics}>
                            {hibernationAndMigrationMetrics.map((metric) => (
                                <div key={metric.id}>
                                    <div className={styles.sectionTitle}>
                                        <div className={styles.blackDot} />
                                        {_(metric.name)}
                                    </div>
                                    <div className={styles.migrationValue}>
                                        {hibernationAndMigrationChoices[metric.value]}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {fallowManagementMetrics && !!fallowManagementMetrics.length && (
                    <ExpandableListItem expanded={false} label={<ItemOverlayTitle label={_('Manejo en barbecho')} />}>
                        <ColoredMetrics
                            metrics={fallowManagementMetrics}
                            choices={controlMetricChoices}
                            colorChoices={textColorChoices}
                        />
                    </ExpandableListItem>
                )}
                {controlStrategiesMetrics && !!controlStrategiesMetrics.length && (
                    <ExpandableListItem
                        expanded={false}
                        label={<ItemOverlayTitle label={_('Estrategias de control')} />}
                    >
                        <ColoredMetrics
                            metrics={controlStrategiesMetrics}
                            choices={controlStrategiesChoices}
                            colorChoices={textColorChoices}
                        />
                    </ExpandableListItem>
                )}
                {chemicalControlStrategiesMetrics && !!chemicalControlStrategiesMetrics.length && (
                    <ExpandableListItem
                        expanded={false}
                        label={<ItemOverlayTitle label={_('Estrategias de Control químico')} />}
                    >
                        <ColoredMetrics
                            metrics={chemicalControlStrategiesMetrics}
                            choices={controlMetricChoices}
                            colorChoices={textColorChoices}
                        />
                    </ExpandableListItem>
                )}
                {managementRecommendations && (
                    <ExpandableListItem expanded={false} label={<ItemOverlayTitle label={_('Recomendaciones')} />}>
                        <div className={styles.managementRecommendation}>{managementRecommendations}</div>
                    </ExpandableListItem>
                )}
            </div>
        );
    }
}
